import {Typography, InlineCheckbox, BorderedTextField, MdRenderer} from '@hconnect/uikit'
import {Box, FormControlLabel, Button} from '@material-ui/core'
import ErrorIcon from '@material-ui/icons/BlockOutlined'
import CloseIcon from '@material-ui/icons/Close'
import EditIcon from '@material-ui/icons/Edit'
import ArrowRightAlt from '@mui/icons-material/ArrowRightAlt'
import {ButtonBase, Grid, Hidden, IconButton, styled} from '@mui/material'
import {isEmpty, some} from 'lodash'
import moment from 'moment'
import React, {useEffect, useState} from 'react'
import {Controller, useFormContext, useFormState} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {usePersistedOrderRequests} from '../../../common/react-query/hooks/queries/usePersistedOrderRequests'
import {useOrderIntakeStyles} from '../../../Hooks/OrderIntake/useOrderIntakeStyles'
import {OrderPlacementDisclaimer} from '../../../OrderIntake/components/OrderPlacementDisclaimer'
import {ShippingAddress} from '../../../OrderIntake/components/ShippingAddress'
import {Features, useFeaturesState} from '../../../Organisms/Features'
import {BulkOrderFormValues} from '../../BulkOrdersFormValues'
import {SummaryNoValidMaterials} from '../../components/SummaryNoValidMaterials'
import {MaterialSectionHeader} from '../../components/VisualComponents'
import {ShippingType} from '../../declarations/OrderIntake.enums'
import {OrderRequest, OrdersFormValidityState, QuantityType} from '../../declarations/types'
import {useBulkOrderFormChangeHandler} from '../../FormChangeHandlers'
import {useOrderIntakeData} from '../../hooks'
import {useCollectionOptions, useDeliveryOptions} from '../../Options'
import {
  isInvalidOrderIntakeOption,
  useDefaultCollection,
  useDefaultDelivery
} from '../../Options/query'
import {getMaterialOptionsByInvalidity} from '../utils'
import {Banner} from '../../../features/rmc/orderIntake/components/shared/Banner'
import {SelectedSplitLoadMaterials} from './SplitLoad/types'

const CloseButton = styled(ButtonBase)`
  position: absolute;
  right: 0;
  top: 0;
  color: ${({theme}) => theme.palette.primary.main};
  z-index: 1000;
`

const getConfirmationMailFeatureKey = (shippingType: ShippingType) =>
  shippingType === ShippingType.DELIVER
    ? 'OrderIntakeShowConfirmationMailDelivery'
    : 'OrderIntakeShowConfirmationMailCollect'

const ArrowRightStyled = styled(ArrowRightAlt)`
  margin-right: 5px;
`

type Props = {
  shippingType: ShippingType
  quantityType: QuantityType
  customerId?: string
  isAllOrdersValid: boolean
  onCloseSummaryBox?: () => void
  onCustomerRefChange: (onCustomerRefChange: string) => void
  onEmailCcChange: (emailAddress: string) => void
  onEditItem: (index: number) => void
  onRevalidateForm: (invalidOrders: OrdersFormValidityState[]) => void
  canShowExpiredWindowAlert?: boolean
}

// eslint-disable-next-line complexity
export const SplitLoadOrderSummarySection: React.FC<Props> = ({
  customerId,
  shippingType,
  isAllOrdersValid,
  onEmailCcChange,
  onCloseSummaryBox,
  onEditItem,
  onRevalidateForm,
  canShowExpiredWindowAlert
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation()
  const {userEmail} = useOrderIntakeData()
  const {getFeature} = useFeaturesState()

  const isSlotsManagementEnabled = getFeature('OrderIntakeSlotsCollect')
  const nextDayCollect = getFeature('OrderIntakeChangeOrderDefaultDayCollect')
  const applyMaterialEnteredDescription = getFeature('OrderIntakeMaterialEnteredDescription')
  const applyContractItemDescription = getFeature('OrderIntakeContractItemDescription')

  const configuration = {
    isSlotsManagementEnabled,
    nextDayCollect,
    applyMaterialEnteredDescription,
    applyContractItemDescription
  }

  const {data: deliverOptions} = useDeliveryOptions(configuration, customerId)
  const {data: collectOptions} = useCollectionOptions(configuration, customerId)

  const methods = useFormContext<BulkOrderFormValues>()
  const classes = useOrderIntakeStyles()

  const {isValid, isValidating, isSubmitting} = useFormState({
    control: methods.control
  })

  const [hasDriverInstructions, setHasDriverInstructions] = React.useState(false)

  const siteNumber = methods.watch('selectedSite.shippingAddress.siteNumber')
  const {data: defaultDelivery} = useDefaultDelivery(customerId, configuration, siteNumber)
  const {data: defaultCollect} = useDefaultCollection(customerId, configuration, siteNumber)

  const {selectedSite, isOrderRequestConfirmed, isConfirmationMailChecked, orders} = methods.watch()
  const selectedSplitLoadMaterialsFromForm = methods.watch('selectedSplitLoadMaterials')

  const {isFetching: isOrderRequestsCacheLoading} = usePersistedOrderRequests(
    selectedSite.shippingAddress.siteNumber
  )
  const options = shippingType === ShippingType.DELIVER ? deliverOptions : collectOptions
  const {onConfirmOrderRequest} = useBulkOrderFormChangeHandler()
  const [open, setOpen] = useState(true)

  const showExpiredBanner = open && canShowExpiredWindowAlert

  const checkForEmptyCustomerRefs = (orders: OrderRequest[]) => {
    return orders.reduce<OrdersFormValidityState[]>((acc, order, index) => {
      if (!order.payload.customerReference || order.payload.customerReference.trim() === '') {
        acc.push({isFormInvalid: true, index})
      }
      return acc
    }, [])
  }

  useEffect(() => {
    const hasDriverInstructions = orders.some((collection) => {
      const driverInstructions = collection.payload?.additionalDriverInfo?.driverInstructions
      return !!driverInstructions && driverInstructions !== ''
    })
    setHasDriverInstructions(!!hasDriverInstructions)
    onRevalidateForm(checkForEmptyCustomerRefs(orders))
  }, [orders])

  if (!options) return null

  const invalidMaterialOptions = getMaterialOptionsByInvalidity(selectedSite.materials, true)
  const validMaterialOptions = getMaterialOptionsByInvalidity(selectedSite.materials, false)
  const noValidMaterial =
    (invalidMaterialOptions.length >= 1 && isEmpty(validMaterialOptions)) ||
    (shippingType === ShippingType.DELIVER
      ? isInvalidOrderIntakeOption(defaultDelivery)
      : isInvalidOrderIntakeOption(defaultCollect))

  const isConfirmationMailDisabled = !selectedSite.canDisableEmailSending
  const isTimeForOrderOver = methods.watch('isTimeForOrderOver')
  const isNoDatesAvailable = some(orders, 'isNoDatesAvailable')
  const isSlotManagementEnabled = getFeature(
    shippingType === ShippingType.DELIVER ? 'OrderIntakeSlotsDeliver' : 'OrderIntakeSlotsCollect'
  )
  const emailCc = methods.watch('contact.carbonCopyEmail')

  const renderSplitLoadOrderItem = (
    order: OrderRequest,
    splitLoadMaterials: SelectedSplitLoadMaterials | undefined,
    index: number
  ) => {
    if (!splitLoadMaterials || !splitLoadMaterials.materials?.length) {
      return null
    }

    // Calculate total quantity
    const totalQuantity = splitLoadMaterials.materials.reduce(
      (sum, material) => sum + (material.capacity?.quantity || 0),
      0
    )

    // Format date and time
    const deliveryDate = moment(order.payload.deliveryDate).locale(language).format('DD.MM.YYYY')
    const timeEarliest = order.payload.deliveryTime?.earliest
      ? moment(order.payload.deliveryTime.earliest, 'HH:mm:ss').format('HH:mm')
      : ''
    const timeLatest = order.payload.deliveryTime?.latest
      ? moment(order.payload.deliveryTime.latest, 'HH:mm:ss').format('HH:mm')
      : ''
    const timeRange = timeEarliest && timeLatest ? `${timeEarliest}-${timeLatest}` : ''

    const handleEditClick = () => {
      onEditItem(index)
    }

    return (
      <Box key={`split-load-order-${index}`} marginTop={4}>
        {/* Date and Time Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={1}>
          <Typography variant="h3" style={{fontWeight: 600}}>
            {deliveryDate}
            {timeRange && `, ${timeRange}`}
          </Typography>
          <IconButton
            size="small"
            style={{border: 'none', boxShadow: 'none'}}
            onClick={handleEditClick}
            data-test-id={`split-load-edit-button-${index}`}
          >
            <EditIcon color="secondary" />
          </IconButton>
        </Box>

        {/* Materials List */}
        <Box>
          {splitLoadMaterials.materials.map((material, materialIndex) => (
            <Box
              key={`material-${index}-${materialIndex}`}
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              paddingY={1}
              borderBottom={
                materialIndex < splitLoadMaterials.materials.length - 1
                  ? '1px solid rgba(0, 0, 0, 0.12)'
                  : 'none'
              }
            >
              <Box flex={1}>
                <Typography variant="body1" style={{fontWeight: 400}}>
                  {material.materialDescription || material.materialNumber}
                </Typography>
                {material.palletSize && (
                  <Typography variant="body2" color="textSecondary">
                    {t('orderIntake.palletSize')}: {material.palletSize}{' '}
                    {material.truckCapacity.capacityUom}
                  </Typography>
                )}
              </Box>
              <Box marginLeft={2}>
                <Typography variant="body1" style={{fontWeight: 500}}>
                  {material.capacity?.quantity?.toFixed(1)} {material.truckCapacity.capacityUom}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Total Quantity */}
        <Box
          display="flex"
          justifyContent="flex-end"
          marginTop={1}
          paddingTop={1}
          borderTop="2px solid rgba(0, 0, 0, 0.2)"
        >
          <Typography variant="h4" style={{fontWeight: 600}}>
            {t('orderIntake.splitLoad.total')}: {totalQuantity.toFixed(1)}{' '}
            {splitLoadMaterials.materials[0]?.truckCapacity.capacityUom}
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Hidden smUp>
        <Grid item sm={12}>
          <CloseButton
            data-test-id="order-intake-summary-close"
            aria-label="Close"
            onClick={onCloseSummaryBox}
          >
            <CloseIcon />
          </CloseButton>
        </Grid>
      </Hidden>

      <Box>
        <MaterialSectionHeader>{t('orderIntake.orderSummary')}</MaterialSectionHeader>
        {/* Info Banner if timer is expired*/}
        {showExpiredBanner && (
          <Banner
            variant="info"
            message={t('orderIntake.rmc.steps.shared.validationErrors.reservationWindowAlertTitle')}
            onClose={() => setOpen(false)}
          />
        )}
        <Box>
          {noValidMaterial ? (
            <Box display="flex" justifyContent="center" marginTop={2}>
              <ErrorIcon color="error" style={{fontSize: 14}} />
              <Box p={0.25} />
              <Typography
                variant="caption"
                customColor="textError"
                data-test-id="order-intake-no-valid-materials"
              >
                {t('orderIntake.invalidMaterials.deliveryNoPossibleForDestination')}
              </Typography>
            </Box>
          ) : null}
        </Box>
        {!noValidMaterial ? (
          <>
            {!isOrderRequestsCacheLoading ? (
              <Box marginTop={5} marginBottom={5}>
                {/* Split Load Orders Summary */}
                {orders.map((order, index) =>
                  renderSplitLoadOrderItem(
                    order,
                    selectedSplitLoadMaterialsFromForm?.[index],
                    index
                  )
                )}
              </Box>
            ) : null}
            <Box mt={3}>
              <ShippingAddress
                showInLine={false}
                headline={
                  shippingType === ShippingType.DELIVER
                    ? t('orderIntake.deliverTo')
                    : t('orderIntake.collectionAddress')
                }
                name={selectedSite.shippingAddress.siteName}
                street={selectedSite.shippingAddress.street}
                postalCode={selectedSite.shippingAddress.postalCode}
                city={selectedSite.shippingAddress.city}
              />
            </Box>
          </>
        ) : (
          <SummaryNoValidMaterials
            invalidMaterialOptions={
              invalidMaterialOptions?.length > 0 ? invalidMaterialOptions : validMaterialOptions
            }
          />
        )}
      </Box>
      {!noValidMaterial && (
        <>
          <Box marginTop={2}>
            <Controller
              control={methods.control}
              name={'contact.carbonCopyEmail'}
              rules={{
                pattern: {
                  message: t('orderIntake.invalidEmailAddress'),
                  value:
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                },
                validate: (value) => {
                  return (
                    value?.toLowerCase() !== userEmail?.toLowerCase() ||
                    t('orderIntake.emailCcMustBeDifferent')
                  )
                }
              }}
              render={({field, fieldState}) => {
                return (
                  <BorderedTextField
                    data-test-id="email-cc-text-field"
                    name={field.name}
                    label={t('orderIntake.emailCc')}
                    required={!!fieldState.error}
                    requiredLabel={fieldState.error ? fieldState.error.message : undefined}
                    placeholder={t('orderIntake.recipientEmailAddress')}
                    value={field.value ?? ''}
                    onChange={(e) => onEmailCcChange(e.target.value)}
                    onKeyDown={(e) => e.code === 'Enter' && e.stopPropagation()}
                    error={!!fieldState.error}
                    warning={isEmpty(emailCc) && hasDriverInstructions}
                    warningLabel={t('orderIntake.emailCcWarningNote')}
                    InputLabelProps={{shrink: !isEmpty(emailCc)}}
                  />
                )
              }}
            />
          </Box>
          <Features name={getConfirmationMailFeatureKey(shippingType)}>
            <Box marginTop={2}>
              <Controller
                control={methods.control}
                name="isConfirmationMailChecked"
                render={({field}) => (
                  <FormControlLabel
                    control={
                      <Box display="flex" mr={0.5}>
                        <InlineCheckbox
                          name={field.name}
                          checked={isConfirmationMailChecked}
                          disabled={isConfirmationMailDisabled || isOrderRequestsCacheLoading}
                          onChange={field.onChange}
                          data-test-id="order-intake-confirmation-mail"
                        />
                      </Box>
                    }
                    label={
                      <Typography variant="body2">
                        {t('orderIntake.sendConfirmationEmail')}
                      </Typography>
                    }
                  />
                )}
              />

              {isConfirmationMailDisabled ? (
                <Box marginTop={1}>
                  <Typography variant="body2">
                    {t('orderIntake.noLongerReceiveConfirmationEmail')}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Features>
        </>
      )}

      {!noValidMaterial ? (
        <Box display="flex" flexDirection="column">
          {!isOrderRequestConfirmed && (
            <Box marginTop={2}>
              <OrderPlacementDisclaimer
                onClick={onConfirmOrderRequest}
                data-test-id="order-intake-confirm-button"
              />
            </Box>
          )}
        </Box>
      ) : null}

      {isSlotManagementEnabled ? (
        <Box pt={2}>
          <MdRenderer body={t('orderIntake.disclaimer')} paragraphVariant="caption" />
        </Box>
      ) : null}

      {/* Error Banner if timer is expired for user to select a slot */}
      {canShowExpiredWindowAlert && (
        <Banner
          variant="error"
          message={t('orderIntake.rmc.steps.shared.validationErrors.slotRequired')}
          testId="slot-selection-banner"
        />
      )}
      <Box mt={2} mb={2}>
        <Button
          type="submit"
          data-test-id="order-intake-submit-button"
          disabled={
            !isValid ||
            isValidating ||
            isSubmitting ||
            !isOrderRequestConfirmed ||
            noValidMaterial ||
            isTimeForOrderOver ||
            isNoDatesAvailable ||
            isOrderRequestsCacheLoading ||
            !isAllOrdersValid ||
            canShowExpiredWindowAlert
          }
          className={classes.orderButton}
          color="primary"
        >
          <ArrowRightStyled />
          {t('orderIntake.placeOrder')}
        </Button>
      </Box>
    </>
  )
}
